// frontend_app/src/App.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

// Import Components
import Header from './components/Header';
import StatusPanel from './components/StatusPanel';
import FederatedSearchPage from './components/FederatedSearchPage';
import { DetailsModal } from './components/DetailsModal';
import LoginPage from './components/LoginPage';
import AdminPage from './components/AdminPage';
import SettingsPanel from './components/SettingsPanel';
import LinksPanel from './components/LinksPanel';

// Import API and Hooks
import { streamSearch, fetchSourcesConfig, fetchDetails, fetchSourcesStatus, login, logout, verify } from './services/api';
import { useModalManager } from './hooks/useModalManager';
import { isActualDisplayableResultItem } from './utils/helpers';

const MIN_SEARCH_LENGTH = 3;

function App() {
  // --- STATE MANAGEMENT ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); // { username, role }
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState('');
  const { modalData, openModal, closeModal } = useModalManager();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLinksPanelOpen, setIsLinksPanelOpen] = useState(false);
  const eventSourceRef = useRef(null);
  const fetchDetailsAbortControllerRef = useRef(null);
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const [lastMainQuery, setLastMainQuery] = useState('');
  const [refineSearchTerm, setRefineSearchTerm] = useState('');
  const [allSitePackages, setAllSitePackages] = useState([]);
  const [clientFilteredGroups, setClientFilteredGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allSourcesConfig, setAllSourcesConfig] = useState([]);
  const [enabledSources, setEnabledSources] = useState({});
  const [sourcesStatus, setSourcesStatus] = useState({});
  
  // --- SETTINGS STATE ---
  const [searchMode, setSearchMode] = useState('local');
  const [isDeepSearch, setIsDeepSearch] = useState(false);
  const [isHighlightingEnabled, setIsHighlightingEnabled] = useState(false);
  const [isSampleHighlightingEnabled, setIsSampleHighlightingEnabled] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('lsearch-theme') || 'dark');
  const [isRefineVisible, setIsRefineVisible] = useState(false);
  
  // --- NEW & UPDATED SETTINGS STATE ---
  const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem('lsearch-fontsize') || '16', 10));
  const [density, setDensity] = useState(() => localStorage.getItem('lsearch-density') || 'compact');

  const location = useLocation();
  const isAdminPage = location.pathname === '/admin';

  // --- EFFECTS ---

  useEffect(() => {
    const checkAuth = async () => {
      try { 
        const response = await verify(); 
        setUser(response.user);
        setIsAuthenticated(true); 
      }
      catch (error) { 
        setIsAuthenticated(false); 
        setUser(null);
      }
      finally { setIsAuthLoading(false); }
    };
    checkAuth();

    const handleAuthExpired = () => {
      setIsAuthenticated((prev) => {
        if (prev) {
          setAuthMessage('Session expired. Please sign in again.');
        }
        return false;
      });
      setUser(null);
    };

    window.addEventListener('lsearch-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('lsearch-auth-expired', handleAuthExpired);
  }, []);
  
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('lsearch-theme', theme);
  }, [theme]);

  useEffect(() => {
    // This makes all Tailwind rem-based utilities scale!
    document.documentElement.style.fontSize = `${(fontSize / 16) * 100}%`;
    localStorage.setItem('lsearch-fontsize', fontSize);
  }, [fontSize]);

  useEffect(() => {
    document.body.setAttribute('data-density', density);
    localStorage.setItem('lsearch-density', density);
  }, [density]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const loadInitialData = async () => {
      try {
        const [config, statusData] = await Promise.all([fetchSourcesConfig(), fetchSourcesStatus()]);
        setAllSourcesConfig(config);
        const initialEnabledState = config.reduce((acc, source) => {
          if (source.display_group === 'Custom Databases' && source.subfolders) {
            acc[source.display_group] = {
              enabled: true,
              folders: source.subfolders.reduce((folderAcc, folderName) => {
                folderAcc[folderName] = true;
                return folderAcc;
              }, {})
            };
          } else {
            acc[source.display_group] = true;
          }
          return acc;
        }, {});
        setEnabledSources(initialEnabledState);
        const statusMap = (statusData || []).reduce((acc, src) => {
          acc[src.name] = src;
          return acc;
        }, {});
        setSourcesStatus(statusMap);
      } catch (e) {
        setError("Could not load initial configuration from the server.");
      }
    };
    loadInitialData();
  }, [isAuthenticated]);

  useEffect(() => {
    const allDisplayableResults = allSitePackages
      .flatMap(pkg => pkg.results || [])
      .filter(isActualDisplayableResultItem);

    let resultsToGroup = allDisplayableResults;
    if (refineSearchTerm.trim()) {
      const lowerRefineTerm = refineSearchTerm.toLowerCase();
      resultsToGroup = allDisplayableResults.filter(result =>
        JSON.stringify(result).toLowerCase().includes(lowerRefineTerm)
      );
    }
    const grouped = resultsToGroup.reduce((acc, result) => {
      const groupKey = result.display_group || result.source;
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(result);
      return acc;
    }, {});
    setClientFilteredGroups(grouped);
  }, [refineSearchTerm, allSitePackages]);

  // --- HANDLERS ---

  const handleLogin = async (username, password) => {
    try {
      await login(username, password);
      const response = await verify();
      console.log("Login verified user:", response.user);
      setUser(response.user);
      setIsAuthenticated(true);
      setAuthMessage('');
    } catch (err) {
      throw err;
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsAuthenticated(false);
    setUser(null);
  };
  
  const handleCustomFolderToggle = (folderName) => {
    setEnabledSources(prev => {
      const customDbState = prev['Custom Databases'];
      const updatedFolders = { ...customDbState.folders, [folderName]: !customDbState.folders[folderName] };
      return { ...prev, 'Custom Databases': { ...customDbState, folders: updatedFolders } };
    });
  };

  const handleShowDetails = async (result) => {
    const hasDetails = (result.details && Object.keys(result.details).length > 0) || (result.inline_details && Object.keys(result.inline_details).length > 0);
    if (hasDetails) { openModal(result); }
    else {
      openModal({ ...result, isLoading: true });
      if (fetchDetailsAbortControllerRef.current) {
         fetchDetailsAbortControllerRef.current.abort();
      }
      fetchDetailsAbortControllerRef.current = new AbortController();
      try {
        const fetchedDetails = await fetchDetails(result.link, result.source, fetchDetailsAbortControllerRef.current.signal);
        openModal({ ...result, details: fetchedDetails, isLoading: false });
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') return;
        openModal({ ...result, error: fetchError.message, isLoading: false });
      }
    }
  };

  const handleCloseModal = () => {
    if (fetchDetailsAbortControllerRef.current) {
        fetchDetailsAbortControllerRef.current.abort();
        fetchDetailsAbortControllerRef.current = null;
    }
    closeModal();
  };
  
  const handleSearch = (searchQuery) => {
    const term = typeof searchQuery === 'string' ? searchQuery : mainSearchTerm;
    const trimmedTerm = term.trim();
    if (trimmedTerm.length < MIN_SEARCH_LENGTH) return;
    const sourcesToSearch = [];
    let enabledCustomFolders = [];
    allSourcesConfig.forEach(source => {
      const groupState = enabledSources[source.display_group];
      if (groupState) {
        if (source.display_group === 'Custom Databases' && typeof groupState === 'object' && groupState.enabled) {
          sourcesToSearch.push(source.source_name);
          enabledCustomFolders = Object.entries(groupState.folders).filter(([, isEnabled]) => isEnabled).map(([folderName]) => folderName);
        } else if (groupState === true) {
          sourcesToSearch.push(source.source_name);
        }
      }
    });
    if (sourcesToSearch.length === 0) return;
    setLastMainQuery(trimmedTerm);
    setRefineSearchTerm('');
    setLoading(true);
    setError(null);
    setAllSitePackages([]);
    if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
    }
    const eventSource = streamSearch(trimmedTerm, [...new Set(sourcesToSearch)], searchMode, enabledCustomFolders, isDeepSearch, (newPackage) => setAllSitePackages(prev => [...prev, newPackage]), () => setLoading(false), (err) => { setError(err); setLoading(false); });
    eventSourceRef.current = eventSource;
  };

  const handleStopSearch = () => { if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; setLoading(false); } };

  // --- MEMOIZED VALUES ---

  const isSearchDisabled = useMemo(() => {
    return Object.values(enabledSources).every(v => (typeof v === 'object' && v !== null) ? !v.enabled : v === false);
  }, [enabledSources]);

  const totalDisplayableItems = useMemo(() => Object.values(clientFilteredGroups).reduce((sum, group) => sum + group.length, 0), [clientFilteredGroups]);
  const expandAllResults = totalDisplayableItems > 0 && totalDisplayableItems < 5;

  // --- RENDER LOGIC ---

  if (isAuthLoading) { return <div className="flex items-center justify-center h-screen w-screen text-sm font-sans" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-faint)' }}>Loading...</div>; }
  if (!isAuthenticated) { return <LoginPage onLogin={handleLogin} authMessage={authMessage} />; }

  return (
    <div className="flex h-screen font-sans antialiased overflow-hidden" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <DetailsModal 
        result={modalData} 
        onClose={handleCloseModal} 
        searchTerm={isHighlightingEnabled ? (refineSearchTerm || lastMainQuery) : ''}
        enableSampleHighlighting={isSampleHighlightingEnabled}
      />

      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        theme={theme} onThemeChange={() => setTheme(p => p === 'dark' ? 'light' : 'dark')}
        fontSize={fontSize} onFontSizeChange={setFontSize}
        density={density} onDensityChange={setDensity}
        isHighlightingEnabled={isHighlightingEnabled} onToggleHighlighting={() => setIsHighlightingEnabled(p => !p)}
        isSampleHighlightingEnabled={isSampleHighlightingEnabled} onToggleSampleHighlighting={() => setIsSampleHighlightingEnabled(p => !p)}
        onLogout={handleLogout}
        user={user}
      />

      <LinksPanel
        isOpen={isLinksPanelOpen}
        onClose={() => setIsLinksPanelOpen(false)}
      />

      {!isAdminPage && (
        <div className="w-60 flex flex-col h-full flex-shrink-0" style={{ backgroundColor: 'var(--bg-surface)', borderRight: '1px solid var(--border-primary)' }}>
          <StatusPanel
            allSitePackages={allSitePackages}
            allSourcesConfig={allSourcesConfig}
            enabledSources={enabledSources}
            onSourceToggle={(group) => setEnabledSources(prev => {
              const current = prev[group];
              if (typeof current === 'object') { return { ...prev, [group]: { ...current, enabled: !current.enabled } }; }
              return { ...prev, [group]: !current };
            })}
            onSelectAll={() => setEnabledSources(prev => {
              const newState = {};
              for (const key in prev) {
                const currentValue = prev[key];
                if (typeof currentValue === 'object' && currentValue !== null && 'folders' in currentValue) {
                  const newFolders = {};
                  for (const folderKey in currentValue.folders) { newFolders[folderKey] = true; }
                  newState[key] = { enabled: true, folders: newFolders };
                } else { newState[key] = true; }
              }
              return newState;
            })}
            onDeselectAll={() => setEnabledSources(prev => {
              const newState = {};
              for (const key in prev) {
                const currentValue = prev[key];
                if (typeof currentValue === 'object' && currentValue !== null && 'folders' in currentValue) {
                  newState[key] = { ...currentValue, enabled: false };
                } else { newState[key] = false; }
              }
              return newState;
            })}
            sourcesStatus={sourcesStatus}
            onCustomFolderToggle={handleCustomFolderToggle}
          />
        </div>
      )}
      
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <Header
          onSettingsClick={() => setIsSettingsOpen(true)}
          onLinksClick={() => setIsLinksPanelOpen(true)} 
          theme={theme}
          onThemeChange={() => setTheme(p => p === 'dark' ? 'light' : 'dark')}
          mainSearchTerm={mainSearchTerm}
          onMainSearchChange={(e) => setMainSearchTerm(e.target.value)}
          onPrimaryAction={loading ? handleStopSearch : handleSearch}
          loading={loading}
          isSearchDisabled={isSearchDisabled}
          refineSearchTerm={refineSearchTerm}
          onRefineChange={(e) => setRefineSearchTerm(e.target.value)}
          searchMode={searchMode}
          onSearchModeChange={() => setSearchMode(prev => prev === 'live' ? 'local' : 'live')}
          isDeepSearch={isDeepSearch}
          onToggleDeepSearch={() => setIsDeepSearch(prev => !prev)}
          isRefineVisible={isRefineVisible}
          onToggleRefine={() => setIsRefineVisible(prev => !prev)}
          user={user}
        />

        <main className="flex-1 overflow-auto relative" style={{ backgroundColor: 'var(--bg-base)' }}>
          <Routes>
            <Route path="/" element={
              <FederatedSearchPage
                clientFilteredGroups={clientFilteredGroups}
                loading={loading}
                error={error}
                lastMainQuery={lastMainQuery}
                refineSearchTerm={refineSearchTerm}
                isHighlightingEnabled={isHighlightingEnabled}
                isSampleHighlightingEnabled={isSampleHighlightingEnabled}
                onShowDetails={handleShowDetails}
                expandAllResults={expandAllResults}
                user={user}
              />}
            />
            <Route path="/admin" element={
              (isAuthenticated && user?.role === 'admin') ? <AdminPage /> : <LoginPage onLogin={handleLogin} authMessage="Admin access required." />
            } />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default App;